/* 
 * SEO Meta Tags:
 * Title: Privacy Policy | InScius
 * Description: Privacy policy and data protection by InScius.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="relative pt-12 pb-8">
        <section className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-24 pb-20">
          <div className="relative z-10">
            {/* Title */}
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
                {t('privacy.header.title')}
              </h1>
              <p className="text-white/60 text-sm">
                {t('privacy.lastUpdated')}
              </p>
            </div>

            {/* Content Container */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10">
              {/* Section 1 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section1.title')}
                </h2>
                <div className="space-y-2">
                  <p className="text-white/80 leading-relaxed">
                    {t('privacy.sections.section1.identity')}
                  </p>
                  <p className="text-white/80 leading-relaxed">
                    {t('privacy.sections.section1.nif')}
                  </p>
                  <p className="text-white/80 leading-relaxed">
                    {t('privacy.sections.section1.address')}
                  </p>
                  <p className="text-white/80 leading-relaxed">
                    {t('privacy.sections.section1.email')}
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section2.title')}
                </h2>
                <p className="text-white/80 leading-relaxed mb-4">
                  {t('privacy.sections.section2.intro')}
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section2.subsection1.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section2.subsection1.text')}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section2.subsection2.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section2.subsection2.text')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section3.title')}
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section3.subsection1.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section3.subsection1.text')}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section3.subsection2.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section3.subsection2.text')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section4.title')}
                </h2>
                <p className="text-white/80 leading-relaxed mb-4">
                  {t('privacy.sections.section4.intro')}
                </p>
                <p className="text-white/80 leading-relaxed mb-4">
                  {t('privacy.sections.section4.paragraph1')}
                </p>
                <ul className="space-y-2 mb-4 ml-6">
                  <li className="text-white/80 leading-relaxed list-disc">
                    {t('privacy.sections.section4.providers.cloud')}
                  </li>
                  <li className="text-white/80 leading-relaxed list-disc">
                    {t('privacy.sections.section4.providers.ai')}
                  </li>
                  <li className="text-white/80 leading-relaxed list-disc">
                    {t('privacy.sections.section4.providers.other')}
                  </li>
                </ul>
                <p className="text-white/80 leading-relaxed">
                  {t('privacy.sections.section4.paragraph2')}
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section5.title')}
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section5.subsection1.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section5.subsection1.text')}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-brand-secondary mb-2">
                      {t('privacy.sections.section5.subsection2.title')}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {t('privacy.sections.section5.subsection2.text')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section6.title')}
                </h2>
                <p className="text-white/80 leading-relaxed">
                  {t('privacy.sections.section6.text')}
                </p>
              </div>

              {/* Section 7 */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('privacy.sections.section7.title')}
                </h2>
                <p className="text-white/80 leading-relaxed mb-4">
                  {t('privacy.sections.section7.paragraph1')}
                </p>
                <p className="text-white/80 leading-relaxed">
                  {t('privacy.sections.section7.paragraph2')}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

